import { useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";

/**
 * Custom hook for standardized access control checking
 *
 * @param {string} module - The module/resource to check access for
 * @returns {Object} - Object containing canView, canEdit, canCreate permissions
 *
 * @example
 * const { canView, canEdit } = useAccessControl(MAIN_COMPUTATION);
 * if (!canView) return <AccessDenied />;
 */
export const useAccessControl = (module = "") => {
  const { checkAccess } = useContext(AuthContext);

  return useMemo(
    () => ({
      canView: checkAccess("view", module),
      canEdit: checkAccess("edit", module),
      canCreate: checkAccess("create", module),
    }),
    [checkAccess, module]
  );
};

/**
 * Hook for checking if user is in viewer-only mode
 * Useful for disabling all editing features
 *
 * @param {string} module - The module/resource to check
 * @returns {boolean} - True if user can only view (not edit)
 */
export const useIsViewOnly = (module = "") => {
  const { checkAccess } = useContext(AuthContext);

  return useMemo(() => {
    const canView = checkAccess("view", module);
    const canEdit = checkAccess("edit", module);
    // User is view-only if they can view but cannot edit
    return canView && !canEdit;
  }, [checkAccess, module]);
};
